import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, catchError, of, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { decodeJwt } from '../utils/decode-jwt';

/** Se renueva el access token si le queda menos que esto. */
export const ACCESS_TOKEN_REFRESH_MARGIN_MS = 60_000;

let sessionRedirectStarted = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const messages = inject(MessageService);
  const token = auth.getAccessToken();
  const isLogin = req.url.includes('/auth/login');
  const isRefresh = req.url.includes('/auth/refresh');
  const esContacto = req.method === 'POST' && esPedidoDeContacto(req.url);

  if (isRefresh || esContacto) {
    return next(req);
  }

  if (!token || isLogin) {
    return next(withToken(req, token));
  }

  const send = (accessToken: string, retried: boolean): Observable<HttpEvent<unknown>> =>
    next(withToken(req, accessToken)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }
        if (retried || !auth.getRefreshToken()) {
          endSession(auth, router, messages);
          return throwError(() => error);
        }
        return auth.refresh().pipe(
          catchError((refreshError: unknown) => {
            if (isUnauthorized(refreshError)) {
              endSession(auth, router, messages);
            }
            return throwError(() => error);
          }),
          switchMap((newToken) => send(newToken, true)),
        );
      }),
    );

  if (isAccessTokenFresh(token)) {
    return send(token, false);
  }

  if (!auth.getRefreshToken()) {
    endSession(auth, router, messages);
    return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Token vencido' }));
  }

  return auth.refresh().pipe(
    catchError((refreshError: unknown) => {
      if (isUnauthorized(refreshError)) {
        endSession(auth, router, messages);
        return throwError(
          () => new HttpErrorResponse({ status: 401, statusText: 'Token vencido' }),
        );
      }
      // Falla de red u otra: si el token todavía sirve, se usa; si no, se informa el error.
      if (isAccessTokenAlive(token)) {
        return of(token);
      }
      return throwError(() => refreshError);
    }),
    switchMap((accessToken) => send(accessToken, true)),
  );
};

function withToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
}

function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
}

function esPedidoDeContacto(url: string): boolean {
  const path = url.split('?')[0];
  return path.endsWith('/contacto') && !path.includes('/muelle/');
}

function tokenExpiresAt(token: string): number | null {
  const payload = decodeJwt(token);
  return payload?.exp ? payload.exp * 1000 : null;
}

function isAccessTokenAlive(token: string): boolean {
  const exp = tokenExpiresAt(token);
  return exp !== null && exp > Date.now();
}

function isAccessTokenFresh(token: string): boolean {
  const exp = tokenExpiresAt(token);
  return exp !== null && exp - ACCESS_TOKEN_REFRESH_MARGIN_MS > Date.now();
}

function endSession(auth: AuthService, router: Router, messages: MessageService): void {
  auth.logout();
  if (sessionRedirectStarted || router.url.startsWith('/login')) {
    return;
  }
  sessionRedirectStarted = true;
  void router.navigateByUrl('/login').finally(() => {
    sessionRedirectStarted = false;
  });
  messages.add({
    severity: 'warn',
    summary: 'Sesión vencida',
    detail: 'Volvé a iniciar sesión.',
    life: 5000,
  });
}
